export type SupportedLanguage = 'en' | 'ru';

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'ru'];
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  ru: 'Русский'
};

export const LANGUAGE_FLAGS: Record<SupportedLanguage, string> = {
  en: '🇺🇸',
  ru: '🇷🇺'
};

export const LANGUAGE_CODES: Record<SupportedLanguage, string> = {
  en: 'EN',
  ru: 'РУ'
};

export interface TranslationSystem {
  t(key: string, params?: Record<string, any>): string;
  setLanguage(lang: SupportedLanguage): Promise<void>;
  getCurrentLanguage(): SupportedLanguage;
  getAvailableLanguages(): SupportedLanguage[];
  isLoading(): boolean;
  getTranslations(): Record<string, string>;
}

export function isValidLanguage(lang: string): lang is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
}

export function getLanguageFromStorage(): SupportedLanguage | null {
  try {
    const stored = localStorage.getItem('vst_shop_language');
    return stored && isValidLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function setLanguageToStorage(language: SupportedLanguage): void {
  try {
    localStorage.setItem('vst_shop_language', language);
  } catch (error) {
    console.error('Failed to save language to localStorage:', error);
  }
}

export function getLanguageFromBrowser(): SupportedLanguage {
  try {
    const browserLanguage = navigator.language.toLowerCase();
    
    if (browserLanguage.startsWith('ru')) return 'ru';
    if (browserLanguage.startsWith('en')) return 'en';
    
    return DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

/**
 * Интерполяция параметров в тексте
 * Поддерживает формат {{variable}} и {variable}
 */
export function interpolateParams(text: string, params?: Record<string, any>): string {
  if (!params) return text;

  return text.replace(/\{\{?(\w+)\}?\}/g, (match, key) => {
    return params[key] !== undefined ? String(params[key]) : match;
  });
}

/**
 * Получить ключ перевода с namespace
 */
export function getTranslationKey(namespace: string, key: string): string {
  return `${namespace}.${key}`;
}

/**
 * Разобрать ключ перевода на namespace и key
 */
export function parseTranslationKey(fullKey: string): { namespace?: string; key: string } {
  const parts = fullKey.split('.');
  if (parts.length === 1) {
    return { key: fullKey };
  }
  
  const namespace = parts.slice(0, -1).join('.');
  const key = parts[parts.length - 1];
  
  return { namespace, key };
} 